import { applyDisplayCurrency, getSavedCurrency, saveCurrency } from '@theme/currency-converter';

class JaydMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[ref="trigger"]');
    this.overlay = this.querySelector('[ref="overlay"]');
    this.drawer = this.querySelector('[ref="drawer"]');

    const root = this.drawer || this;
    this.closeBtn = root.querySelector('[ref="close"]');
    this.panels = root.querySelector('[ref="panels"]');
    this.primaryPanel = root.querySelector('.jayd-menu__panel--primary');
    this.secondaryPanels = root.querySelectorAll('.jayd-menu__panel--secondary');
    this.featurePanels = root.querySelectorAll('.jayd-menu__panel--feature');

    // Escape the sticky header stacking context so overlay + panel cover the page.
    if (this.overlay) document.body.appendChild(this.overlay);
    if (this.drawer) {
      this.drawer.style.cssText += this.getAttribute('style') || '';
      document.body.appendChild(this.drawer);
    }

    this.activeParent = null;

    this.secondaryPanels.forEach((p) => {
      p.hidden = false;
      p.dataset.active = 'false';
    });
    this.featurePanels.forEach((p) => {
      p.hidden = false;
      p.dataset.active = 'false';
    });

    this.initSlider();
    this.bindEvents();
    this.initLocalization();
    this._onKeydown = (e) => {
      if (e.key === 'Escape') this.handleEscape();
    };
  }

  $all(selector) {
    return (this.drawer || this).querySelectorAll(selector);
  }

  bindEvents() {
    this.trigger?.addEventListener('click', () => this.openMenu());
    this.overlay?.addEventListener('click', () => this.closeMenu());
    this.closeBtn?.addEventListener('click', () => this.closeMenu());

    this.$all('.jayd-menu__link--parent').forEach((btn) => {
      btn.addEventListener('click', () => this.openChild(btn.dataset.parentIndex));
    });

    this.$all('.jayd-menu__back--secondary').forEach((btn) => {
      btn.addEventListener('click', () => this.closeChild());
    });

    this.$all('.jayd-menu__back--feature').forEach((btn) => {
      btn.addEventListener('click', () => this.closeChild());
    });
  }

  initLocalization() {
    const root = this.drawer || this;
    const countrySelect = root.querySelector('[data-jayd-country-select]');
    const languageSelect = root.querySelector('[data-jayd-language-select]');
    const currencySelect = root.querySelector('[data-jayd-currency-select]');

    countrySelect?.addEventListener('change', (e) => {
      e.target.closest('form')?.submit();
    });

    languageSelect?.addEventListener('change', (e) => {
      e.target.closest('form')?.submit();
    });

    if (currencySelect) {
      const shopCurrency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'USD';
      currencySelect.value = getSavedCurrency() ?? shopCurrency;

      currencySelect.addEventListener('change', (e) => {
        const value = e.target instanceof HTMLSelectElement ? e.target.value : null;
        if (!value) return;
        saveCurrency(value);
        applyDisplayCurrency(value);
      });
    }
  }

  initSlider() {
    const root = this.drawer || this;
    const slider = root.querySelector('[ref="sliderTrack"]');
    if (!slider) return;

    const prev = root.querySelector('[ref="sliderPrev"]');
    const next = root.querySelector('[ref="sliderNext"]');
    const progress = root.querySelector('[ref="sliderProgress"]');

    const updateProgress = () => {
      if (!progress) return;
      const max = slider.scrollWidth - slider.clientWidth;
      const ratio = max > 0 ? slider.scrollLeft / max : 0;
      progress.style.setProperty('--jayd-slider-progress', ratio);
    };

    const step = () => {
      const card = slider.querySelector('.jayd-menu__slide');
      if (!card) return slider.clientWidth;
      const gap = parseFloat(getComputedStyle(slider).columnGap || 0);
      return card.offsetWidth + gap;
    };

    const dir = () => (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
    prev?.addEventListener('click', () => slider.scrollBy({ left: -dir() * step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => slider.scrollBy({ left: dir() * step(), behavior: 'smooth' }));
    slider.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }

  hasFeature(parent) {
    return Array.from(this.featurePanels).some((p) => p.dataset.parent === String(parent));
  }

  openMenu() {
    this.dataset.menuOpen = 'true';
    this.drawer?.setAttribute('data-open', 'true');
    this.overlay?.classList.add('is-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this._onKeydown);
  }

  closeMenu() {
    this.dataset.menuOpen = 'false';
    this.drawer?.removeAttribute('data-open');
    this.overlay?.classList.remove('is-open');
    this.dataset.depth = '1';
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this._onKeydown);
    this.secondaryPanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    this.featurePanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    setTimeout(() => this.resetPanels(), 450);
  }

  handleEscape() {
    if (this.activeParent) return this.closeChild();
    this.closeMenu();
  }

  resetPanels() {
    if (this.dataset.menuOpen === 'true') return;
    this.activeParent = null;
    this.dataset.depth = '1';
    this.secondaryPanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    this.featurePanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    this.$all('.jayd-menu__link--parent').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  openChild(parentIndex) {
    const key = String(parentIndex);

    this.secondaryPanels.forEach((p) => {
      p.dataset.active = p.dataset.parent === key ? 'true' : 'false';
    });

    let hasFeature = false;
    this.featurePanels.forEach((p) => {
      const match = p.dataset.parent === key;
      if (match) hasFeature = true;
      p.dataset.active = match ? 'true' : 'false';
    });

    this.$all('.jayd-menu__link--parent').forEach((b) => {
      b.setAttribute('aria-expanded', b.dataset.parentIndex === key ? 'true' : 'false');
    });

    this.activeParent = key;
    this.dataset.depth = hasFeature ? '3' : '2';
  }

  closeChild() {
    this.secondaryPanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    this.featurePanels.forEach((p) => {
      p.dataset.active = 'false';
    });
    this.$all('.jayd-menu__link--parent').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    this.activeParent = null;
    this.dataset.depth = '1';
  }
}

customElements.define('jayd-menu', JaydMenu);
